import { useQuery } from "@tanstack/react-query";

import { markLicenseReadonly } from "@/shared/lib/licenseReadonly";

import { isLicenseEnabled } from "../model/licenseRules";
import { licenseService, type LicenseView } from "./licenseService";

export const LICENSE_QUERY_KEY = ["platform-license"] as const;

/** 把授权判定同步给全站只读横幅:READONLY 置位 / OK 清除;DISABLED 不动。上传 / 心跳回包也走这里。 */
export function syncLicenseReadonly(v: LicenseView | null | undefined): void {
  const mode = v?.status?.mode;
  if (mode === "READONLY") markLicenseReadonly(true);
  else if (mode === "OK") markLicenseReadonly(false);
}

/** 拉当前授权判定,顺手同步只读信号。 */
export async function fetchLicenseView(): Promise<LicenseView> {
  const v = await licenseService.get();
  syncLicenseReadonly(v);
  return v;
}

/**
 * 授权状态查询(授权页 + 侧栏入口共用一个 key)。
 * `enabled=false` 时不发请求 —— 侧栏只给平台超管(非代客)探,租户 / 渠道一律不打这个 /platform 接口。
 */
export function useLicenseView(enabled = true) {
  return useQuery<LicenseView>({
    queryKey: [...LICENSE_QUERY_KEY],
    queryFn: fetchLicenseView,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    // 侧栏探针失败(老后端 / 网络)不该弹全局 toast:入口按「未启用」藏起来即可。
    meta: { suppressGlobalErrorToast: true },
  });
}

/** 侧栏「授权」入口是否出现:只有后端说 license 启用(mode ≠ DISABLED)才出现。 */
export function useLicenseEnabled(enabled: boolean): boolean {
  const q = useLicenseView(enabled);
  return enabled && isLicenseEnabled(q.data);
}
